// Buyer-authored sticky notes. Phase 8 "comments on rooms" first slice.
//
// Data model: NoteHotspot is in src/app/types.ts; it's part of the Hotspot
// discriminated union so the existing HotspotManager renders it as a 3D dot
// + HTML label without a parallel pipeline.
//
// Storage: an in-memory Map<roomId, NoteHotspot[]> is the source of truth
// at runtime. A localStorage shadow keyed by buyer-session code (or a
// session-less "draft" key) persists across reloads even before the buyer
// taps 💾 Save Session. When BuyerSession ships, App captures all() into
// the session payload + applies replaceAll() on hydrate.
//
// IDs: monotonic `note-<base36-timestamp>` — collisions across two buyers
// editing the same `?s=<code>` are vanishingly rare and a duplicate just
// renders as a second dot in the same spot (the rep can delete it).

import type { NoteHotspot } from './types';

const STORAGE_DRAFT_KEY = 'imersivo:notes-draft';

export class NotesManager {
  /** roomId → notes in that room. */
  private byRoom = new Map<string, NoteHotspot[]>();

  /** Optional storage key suffix — set to the buyer's session code when
   *  a session is saved so notes scope to that buyer's URL, not the device. */
  private storageScope: string | null = null;

  constructor() {
    this.loadFromStorage();
  }

  /** Return the notes for a room (empty array if none). Never returns null. */
  forRoom(roomId: string): NoteHotspot[] {
    return this.byRoom.get(roomId) ?? [];
  }

  /** Flat list of every note across every room — used by BuyerSession.notes. */
  all(): NoteHotspot[] {
    const out: NoteHotspot[] = [];
    for (const list of this.byRoom.values()) out.push(...list);
    return out;
  }

  /**
   * Create a new note at the given yaw/pitch in the given room. Returns
   * the note for the caller to immediately open its editor.
   */
  add(roomId: string, yaw: number, pitch: number, distance = 12): NoteHotspot {
    const id = `note-${Date.now().toString(36)}`;
    const note: NoteHotspot = {
      id,
      type: 'note',
      label: '📝',
      yaw,
      pitch,
      distance,
      text: '',
      roomId,
      updatedAt: new Date().toISOString(),
    };
    const existing = this.byRoom.get(roomId) ?? [];
    existing.push(note);
    this.byRoom.set(roomId, existing);
    this.persist();
    return note;
  }

  /** Update an existing note's text. No-op if the id doesn't resolve. */
  update(noteId: string, text: string): NoteHotspot | null {
    for (const list of this.byRoom.values()) {
      const note = list.find((n) => n.id === noteId);
      if (!note) continue;
      note.text = text;
      note.label = text.length > 0 ? truncate(text, 32) : '📝';
      note.updatedAt = new Date().toISOString();
      this.persist();
      return note;
    }
    return null;
  }

  delete(noteId: string): boolean {
    for (const [roomId, list] of this.byRoom.entries()) {
      const idx = list.findIndex((n) => n.id === noteId);
      if (idx === -1) continue;
      list.splice(idx, 1);
      if (list.length === 0) this.byRoom.delete(roomId);
      this.persist();
      return true;
    }
    return false;
  }

  /** Replace every note (used by BuyerSession hydrate). */
  replaceAll(notes: NoteHotspot[]): void {
    this.byRoom.clear();
    for (const note of notes) {
      const list = this.byRoom.get(note.roomId) ?? [];
      list.push(note);
      this.byRoom.set(note.roomId, list);
    }
    this.persist();
  }

  /** Switch the localStorage shadow to a session-scoped key. Call after a
   *  buyer saves their session — keeps multi-buyer browsers from mixing
   *  notes across `?s=<code>` URLs. */
  setStorageScope(sessionCode: string | null): void {
    this.storageScope = sessionCode;
    this.persist();
  }

  private storageKey(): string {
    return this.storageScope ? `${STORAGE_DRAFT_KEY}:${this.storageScope}` : STORAGE_DRAFT_KEY;
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as NoteHotspot[];
      if (!Array.isArray(parsed)) return;
      this.replaceAll(parsed);
    } catch {
      /* corrupted / Safari private mode — fail silently */
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.all()));
    } catch {
      /* quota / private mode — runtime memory still works */
    }
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}
